import React from 'react';
import type { ReactNode } from 'react';
import { Pressable as RNPressable } from 'react-native';
import { Pressable as GHPressable } from 'react-native-gesture-handler';

import { InnerBorder } from '@/components/InnerBorder';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { EntryCardHeader } from './EntryCardHeader';
import type { EntryCardHeaderProps } from './EntryCardHeader';
import { EntryCardPayout } from './EntryCardPayout';
import type { EntryCardPayoutProps } from './EntryCardPayout';

// RN Pressable avoids nested <button> elements on web; GH Pressable handles native gestures.
const Pressable = isWeb ? RNPressable : GHPressable;

export type EntryCardVariant = 'pickem' | 'sbk';

export type EntryCardProps = {
    variant?: EntryCardVariant;
    header: EntryCardHeaderProps;
    payout: EntryCardPayoutProps;
    promos?: ReactNode[];
    bottom: ReactNode;
    /** Pre-rendered cash-out region (e.g. the SBK inline cash-out state machine); occupies the footer slot. */
    cashOut?: ReactNode;
    onPress?: () => void;
    testID?: string;
};

const SHARED = {
    outerPadding: 's16' as const,
    borderRadius: 'r20' as const,
    borderColor: ds.colorValue.border.subtle,
    backgroundColor: ds.colorToken.background.neutral.subtler,
    headerToPayoutGap: 's12' as const,
    payoutToPromoGap: 's16' as const,
    payoutToBottomGap: 's16' as const,
};

// The promo→bottom and bottom→footer gaps are tighter in the sbk variant (20 vs 24)
const spec = {
    pickem: { ...SHARED, promoToBottomGap: 's24' as const, bottomToFooterGap: 's24' as const },
    sbk: { ...SHARED, promoToBottomGap: 's20' as const, bottomToFooterGap: 's20' as const },
} satisfies Record<
    EntryCardVariant,
    typeof SHARED & { promoToBottomGap: 's20' | 's24'; bottomToFooterGap: 's20' | 's24' }
>;

export const EntryCard = ({
    variant = 'sbk',
    header,
    payout,
    promos = [],
    bottom,
    cashOut,
    onPress,
    testID,
}: EntryCardProps) => {
    const s = spec[variant];

    return (
        <Pressable onPress={onPress} style={common.flex}>
            <Box
                backgroundColor={s.backgroundColor}
                paddingVertical={s.outerPadding}
                borderRadius={s.borderRadius}
                overflow="hidden"
                style={common.flex}
                justifyContent="space-between"
                testID={testID}
            >
                <InnerBorder borderRadius={s.borderRadius} borderColor={s.borderColor} />
                <Box>
                    <EntryCardHeader {...header} />
                    <Box marginTop={s.headerToPayoutGap}>
                        <EntryCardPayout {...payout} />
                    </Box>
                </Box>
                {promos.map((promo, index) => (
                    <Box key={index} marginTop={index === 0 ? s.payoutToPromoGap : 's8'} paddingHorizontal="s16">
                        {promo}
                    </Box>
                ))}
                {bottom != null ? (
                    <Box marginTop={promos.length > 0 ? s.promoToBottomGap : s.payoutToBottomGap}>{bottom}</Box>
                ) : null}
                {cashOut ? (
                    <Box marginTop={s.bottomToFooterGap} paddingHorizontal="s16">
                        {cashOut}
                    </Box>
                ) : null}
            </Box>
        </Pressable>
    );
};
